import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { catchError, EMPTY, map, mergeMap, Observable, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { PortalRoutes } from '@app/app-routing.module';
import { PortalPassengerNode } from '@app/services/api/api.types';
import { AuthorizedDataService } from '@app/services/authorized-data/authorized-data.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { ApiProtectedService } from '@app/services/api/api.protected.service';

@Component({
  selector: 'client-traveler-page',
  templateUrl: './client-traveler-page.component.html',
  styleUrl: './client-traveler-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientTravelerPageComponent implements OnInit {
  location = inject(Location);
  authService = inject(AuthorizedDataService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  apiProtectedService = inject(ApiProtectedService);
  changeDetectorRef = inject(ChangeDetectorRef);

  errors: FormError[] | null;

  passenger$: Observable<PortalPassengerNode | undefined> = this.authService
    .getAuthorizedData()
    .pipe(
      mergeMap((data) =>
        this.route.paramMap.pipe(
          map((params) => {
            const travelerId = params.get(PortalRoutes.TravelerParam);
            const traveler = data?.passengers?.find((passenger) => passenger.id === travelerId);

            if (data && !traveler) {
              return void this.router.navigate([PortalRoutes.NotFound]);
            }

            return traveler;
          }),
        ),
      ),
    );

  ngOnInit() {
    Tracking.track(PortalTrackingEvents.travelerPageOpened);
  }

  goBack(): void {
    Tracking.track(PortalTrackingEvents.travelerPageBackPressed);

    this.location.back();
  }

  formSubmit(data: PortalPassengerNode) {
    const id = this.route.snapshot.paramMap.get(PortalRoutes.TravelerParam) || '';

    const inputData: {
      id: string;
      specialAssistanceId?: string;
      petsId?: string;
      mealPreferencesId?: string;
      seatPreferencesId?: string;
      phones?: string[];
      emails?: string[];
      globalEntry?: string;
      knownTravelerNumber?: string;
      passport?: { number: string; expiration: Date };
      frequentFlyerNumbers?: { number: string; airlineId: string }[];
    } = { id };

    [
      'specialAssistanceId',
      'petsId',
      'mealPreferencesId',
      'seatPreferencesId',
      'phones',
      'emails',
      'globalEntry',
      'knownTravelerNumber',
    ].forEach((key) => {
      const value = data[key as keyof PortalPassengerNode];

      if (value) {
        Object.assign(inputData, { [key]: value });
      }
    });

    if (data.passport?.number || data.passport?.expiration) {
      Object.assign(inputData, {
        passport: {
          number: data.passport?.number,
          expiration: data.passport?.expiration,
        },
      });
    }

    if (data.frequentFlyerNumbers) {
      Object.assign(inputData, {
        frequentFlyerNumbers: data.frequentFlyerNumbers.filter((v) => !!v?.number),
      });
    }

    this.apiProtectedService
      .updatePassenger({ inputData })
      .pipe(
        take(1),
        catchError((error: Error) => {
          console.log(error.message || error);
          Tracking.track(PortalTrackingEvents.travelerPageSubmitError);
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (response?.data?.updatePassenger?.ok) {
          void this.router.navigate([PortalRoutes.Travelers]);
          this.authService.refetchAuthorizedDataEvent();
        } else {
          this.errors = response?.errors;
        }

        this.changeDetectorRef.detectChanges();
      });
  }
}

export type FormError = {
  message: string;
  extensions: {
    field: string;
  };
};
